import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Star, Truck, ShieldCheck, RotateCcw, ChevronRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function ProductDetail() {
  const { handle } = useParams();
  const [p, setP] = useState(null);
  const [variant, setVariant] = useState(null);
  const [img, setImg] = useState(0);
  const { addItem } = useCart();

  const load = useCallback(async () => {
    const r = await api.get(`/products/${handle}`);
    setP(r.data);
    setVariant(r.data.variants[0]);
    setImg(0);
  }, [handle, setP, setVariant, setImg]);

  useEffect(() => {
    load();
  }, [load]);

  if (!p) return <div className="min-h-[60vh] grid place-items-center text-[#86868B]" data-testid="pd-loading">Loading...</div>;

  const compare = variant?.compare_at_price && variant.compare_at_price > variant.price;

  const addToCart = () => {
    addItem({
      product_id: p.id,
      variant_sku: variant.sku,
      product_name: p.name,
      variant_name: variant.name,
      unit_price: variant.price,
      quantity: 1,
      image: p.images[0],
    });
    toast.success(`${p.name} added to cart`);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: p.short_description,
    image: p.images,
    sku: variant?.sku,
    brand: { "@type": "Brand", name: "CreatorMic Pro" },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: p.rating,
      reviewCount: p.review_count,
    },
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: variant?.price,
      availability: "https://schema.org/InStock",
      url: `https://creatormicpro.com/products/${p.handle}`,
    },
  };

  return (
    <div className="fade-in" data-testid="page-product-detail">
      <SEO title={p.name} description={p.short_description} path={`/products/${p.handle}`} image={p.images[0]} type="product" jsonLd={jsonLd} />

      {/* breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8 text-sm text-[#86868B] flex items-center gap-2 flex-wrap" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-[#0066FF]">Home</Link>
        <ChevronRight size={12} />
        <Link to="/shop" className="hover:text-[#0066FF]">Shop</Link>
        <ChevronRight size={12} />
        <Link to={`/shop?category=${p.category}`} className="hover:text-[#0066FF] capitalize">{p.category.replace(/-/g, " ")}</Link>
        <ChevronRight size={12} />
        <span className="text-[#1D1D1F]">{p.name}</span>
      </div>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-6 pb-16 grid md:grid-cols-2 gap-10 md:gap-16">
        {/* GALLERY */}
        <div className="md:sticky md:top-24 self-start">
          <div className="aspect-square rounded-3xl overflow-hidden bg-[#F5F5F7]" data-testid="product-image-main">
            <img src={p.images[img]} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {p.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {p.images.map((src, i) => (
                <button
                  key={src}
                  onClick={() => setImg(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${img === i ? "border-[#0066FF]" : "border-transparent"}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* DETAILS */}
        <div>
          {p.badge && <span className="inline-block px-3 py-1 rounded-full bg-[#0066FF]/10 text-[#0066FF] text-xs font-bold uppercase tracking-widest">{p.badge}</span>}
          <h1 className="font-display text-4xl md:text-5xl font-bold text-[#1D1D1F] mt-4 leading-tight tracking-tight">{p.name}</h1>
          <p className="mt-4 text-[#86868B] text-lg">{p.short_description}</p>

          <div className="flex items-center gap-2 mt-4 text-sm text-[#86868B]">
            <Star size={14} fill="#0066FF" stroke="#0066FF" />
            <span>{p.rating} · {p.review_count} verified reviews</span>
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-display text-3xl font-bold text-[#1D1D1F]" data-testid="product-price">{money(variant?.price)}</span>
            {compare && <span className="text-lg text-[#86868B] line-through">{money(variant.compare_at_price)}</span>}
            {compare && <span className="text-sm text-[#34C759] font-semibold">Save {money(variant.compare_at_price - variant.price)}</span>}
          </div>

          {p.variants.length > 1 && (
            <div className="mt-8">
              <p className="eyebrow mb-3">Choose your option</p>
              <div className="flex flex-wrap gap-2">
                {p.variants.map((v) => (
                  <button
                    key={v.sku}
                    onClick={() => setVariant(v)}
                    data-testid={`variant-${v.sku}`}
                    className={`px-5 py-3 rounded-full border-2 text-sm font-medium transition ${
                      variant?.sku === v.sku ? "border-[#0066FF] bg-[#0066FF]/5 text-[#0066FF]" : "border-black/10 text-[#1D1D1F] hover:border-black/30"
                    }`}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button className="btn-primary flex-1" onClick={addToCart} data-testid="product-add-to-cart">
              Add to cart · {money(variant?.price)}
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 text-xs text-[#86868B]">
            <div className="flex flex-col items-start gap-2 p-4 bg-[#F5F5F7] rounded-2xl">
              <Truck size={16} className="text-[#0066FF]" />
              <span>Free US shipping over $50</span>
            </div>
            <div className="flex flex-col items-start gap-2 p-4 bg-[#F5F5F7] rounded-2xl">
              <RotateCcw size={16} className="text-[#0066FF]" />
              <span>30-day risk-free returns</span>
            </div>
            <div className="flex flex-col items-start gap-2 p-4 bg-[#F5F5F7] rounded-2xl">
              <ShieldCheck size={16} className="text-[#0066FF]" />
              <span>2-year warranty</span>
            </div>
          </div>

          {/* TABS */}
          <Tabs defaultValue="features" className="mt-12">
            <TabsList className="bg-transparent border-b border-black/10 rounded-none w-full justify-start gap-6">
              <TabsTrigger value="features" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0066FF] data-[state=active]:bg-transparent data-[state=active]:text-[#0066FF] px-0" data-testid="tab-features">Features</TabsTrigger>
              <TabsTrigger value="specs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0066FF] data-[state=active]:bg-transparent data-[state=active]:text-[#0066FF] px-0" data-testid="tab-specs">Specs</TabsTrigger>
              <TabsTrigger value="box" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0066FF] data-[state=active]:bg-transparent data-[state=active]:text-[#0066FF] px-0" data-testid="tab-box">In the box</TabsTrigger>
            </TabsList>
            <TabsContent value="features" className="pt-6">
              <p className="text-[#86868B] mb-4">{p.description}</p>
              <ul className="space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#1D1D1F]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2" /> {f}
                  </li>
                ))}
              </ul>
            </TabsContent>
            <TabsContent value="specs" className="pt-6">
              <dl className="divide-y divide-black/5">
                {p.specs.map((s) => (
                  <div key={s.label} className="flex justify-between py-3">
                    <dt className="text-sm text-[#86868B]">{s.label}</dt>
                    <dd className="text-sm text-[#1D1D1F] font-medium">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </TabsContent>
            <TabsContent value="box" className="pt-6">
              <ul className="space-y-2">
                {p.what_in_box.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-[#1D1D1F]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2" /> {b}
                  </li>
                ))}
              </ul>
              <p className="eyebrow mt-8 mb-3">Compatible with</p>
              <div className="flex flex-wrap gap-2">
                {p.compatible_with.map((c) => (
                  <span key={c} className="px-3 py-1.5 rounded-full bg-[#F5F5F7] text-xs text-[#1D1D1F]">{c}</span>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Sticky mobile buy */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-black/5 p-4 flex items-center gap-3 z-40" data-testid="sticky-mobile-buy">
        <div>
          <p className="text-xs text-[#86868B]">{p.name}</p>
          <p className="font-bold">{money(variant?.price)}</p>
        </div>
        <button className="btn-primary flex-1" onClick={addToCart} data-testid="sticky-add-btn">Add to cart</button>
      </div>
    </div>
  );
}
